import React, { useState } from 'react';
import { Mail, Phone, MapPin, Clock, Send } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { companyInfo } from '../mock';
import { toast } from 'sonner';

const Contact = () => {
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });

  const [quoteForm, setQuoteForm] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    projectDetails: ''
  });

  const [isSubmittingContact, setIsSubmittingContact] = useState(false);
  const [isSubmittingQuote, setIsSubmittingQuote] = useState(false);

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setIsSubmittingContact(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(contactForm),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: 'Unknown error' }));
        throw new Error(errorData.detail || 'Failed to submit contact form');
      }

      await response.json(); // Parse the successful response

      toast.success("Message Sent Successfully!", {
        description: "Thank you for contacting QNC Co. Limited. We'll get back to you within 24 hours.",
        duration: 5000,
      });
      setContactForm({ name: '', email: '', phone: '', message: '' });
    } catch (error) {
      console.error('Contact form error:', error);
      toast.error("Failed to Send Message", {
        description: "Please try again or contact us directly at qncconstructions@gmail.com",
        duration: 5000,
      });
    } finally {
      setIsSubmittingContact(false);
    }
  };

  const handleQuoteSubmit = async (e) => {
    e.preventDefault();
    setIsSubmittingQuote(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/quote`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: quoteForm.name,
          email: quoteForm.email,
          phone: quoteForm.phone,
          service: quoteForm.service,
          project_details: quoteForm.projectDetails,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: 'Unknown error' }));
        throw new Error(errorData.detail || 'Failed to submit quote request');
      }

      await response.json(); // Parse the successful response

      toast.success("Quote Request Received!", {
        description: "Thank you! Our team will prepare your detailed quote and contact you within 24-48 hours.",
        duration: 5000,
      });
      setQuoteForm({ name: '', email: '', phone: '', service: '', projectDetails: '' });
    } catch (error) {
      console.error('Quote form error:', error);
      toast.error("Failed to Submit Quote Request", {
        description: "Please try again or contact us directly at qncconstructions@gmail.com",
        duration: 5000,
      });
    } finally {
      setIsSubmittingQuote(false);
    }
  };

  return (
    <section id="contact" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Get In Touch
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Ready to start your project? Contact us or request a quote
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Contact Information */}
          <div className="lg:col-span-1 space-y-6">
            <Card className="border-2 border-gray-100">
              <CardHeader>
                <CardTitle className="text-2xl">Contact Information</CardTitle>
                <CardDescription>Reach out to us anytime</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-start">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    <MapPin className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900 mb-1">Address</div>
                    <div className="text-sm text-gray-600">{companyInfo.address}</div>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    <Phone className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900 mb-1">Phone</div>
                    <div className="text-sm text-gray-600">{companyInfo.phone}</div>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    <Mail className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900 mb-1">Email</div>
                    <div className="text-sm text-gray-600">{companyInfo.email}</div>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    <Clock className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900 mb-1">Hours</div>
                    <div className="text-sm text-gray-600">{companyInfo.hours}</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Forms */}
          <div className="lg:col-span-2">
            <Tabs defaultValue="contact" className="w-full">
              <TabsList className="grid w-full grid-cols-2 mb-6">
                <TabsTrigger value="contact">Contact Form</TabsTrigger>
                <TabsTrigger value="quote">Request Quote</TabsTrigger>
              </TabsList>

              <TabsContent value="contact">
                <Card className="border-2 border-gray-100">
                  <CardHeader>
                    <CardTitle>Send Us a Message</CardTitle>
                    <CardDescription>
                      Have questions? We'd love to hear from you.
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <form onSubmit={handleContactSubmit} className="space-y-4">
                      <div>
                        <Label htmlFor="contact-name">Name</Label>
                        <Input
                          id="contact-name"
                          value={contactForm.name}
                          onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                          placeholder="Your name"
                          required
                        />
                      </div>
                      <div>
                        <Label htmlFor="contact-email">Email</Label>
                        <Input
                          id="contact-email"
                          type="email"
                          value={contactForm.email}
                          onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                          placeholder="your@email.com"
                          required
                        />
                      </div>
                      <div>
                        <Label htmlFor="contact-phone">Phone</Label>
                        <Input
                          id="contact-phone"
                          value={contactForm.phone}
                          onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })}
                          placeholder="+1 (876) XXX-XXXX"
                          required
                        />
                      </div>
                      <div>
                        <Label htmlFor="contact-message">Message</Label>
                        <Textarea
                          id="contact-message"
                          value={contactForm.message}
                          onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                          placeholder="Tell us about your project or inquiry..."
                          rows={5}
                          required
                        />
                      </div>
                      <Button
                        type="submit"
                        className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                        size="lg"
                        disabled={isSubmittingContact}
                      >
                        {isSubmittingContact ? 'Sending...' : 'Send Message'}
                        <Send className="ml-2 w-4 h-4" />
                      </Button>
                    </form>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="quote">
                <Card className="border-2 border-gray-100">
                  <CardHeader>
                    <CardTitle>Request a Quote</CardTitle>
                    <CardDescription>
                      Tell us about your project and we'll provide a detailed quote.
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <form onSubmit={handleQuoteSubmit} className="space-y-4">
                      <div>
                        <Label htmlFor="quote-name">Name</Label>
                        <Input
                          id="quote-name"
                          value={quoteForm.name}
                          onChange={(e) => setQuoteForm({ ...quoteForm, name: e.target.value })}
                          placeholder="Your name"
                          required
                        />
                      </div>
                      <div>
                        <Label htmlFor="quote-email">Email</Label>
                        <Input
                          id="quote-email"
                          type="email"
                          value={quoteForm.email}
                          onChange={(e) => setQuoteForm({ ...quoteForm, email: e.target.value })}
                          placeholder="your@email.com"
                          required
                        />
                      </div>
                      <div>
                        <Label htmlFor="quote-phone">Phone</Label>
                        <Input
                          id="quote-phone"
                          value={quoteForm.phone}
                          onChange={(e) => setQuoteForm({ ...quoteForm, phone: e.target.value })}
                          placeholder="+1 (876) XXX-XXXX"
                          required
                        />
                      </div>
                      <div>
                        <Label htmlFor="quote-service">Service Needed</Label>
                        <Select
                          value={quoteForm.service}
                          onValueChange={(value) => setQuoteForm({ ...quoteForm, service: value })}
                          required
                        >
                          <SelectTrigger id="quote-service">
                            <SelectValue placeholder="Select a service" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="construction">Construction Services</SelectItem>
                            <SelectItem value="prefab">Prefab Steel Buildings</SelectItem>
                            <SelectItem value="generators">Generator Solutions</SelectItem>
                            <SelectItem value="tools">Tools & Equipment</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label htmlFor="quote-details">Project Details</Label>
                        <Textarea
                          id="quote-details"
                          value={quoteForm.projectDetails}
                          onChange={(e) => setQuoteForm({ ...quoteForm, projectDetails: e.target.value })}
                          placeholder="Describe your project requirements, timeline, budget, etc..."
                          rows={5}
                          required
                        />
                      </div>
                      <Button
                        type="submit"
                        className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                        size="lg"
                        disabled={isSubmittingQuote}
                      >
                        {isSubmittingQuote ? 'Submitting...' : 'Request Quote'}
                        <Send className="ml-2 w-4 h-4" />
                      </Button>
                    </form>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;